import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  BellOutlined,
  UserOutlined,
  LeftOutlined,
  SunOutlined,
  MoonOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";

const ModernHeader = ({
  collapsed,
  onToggleSidebar,
  onGoBack,
  title = "Dashboard",
  subtitle = "",
}) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const { userData } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications] = useState([
    {
      id: 1,
      message: "New bond listing available",
      time: "2 min ago",
      unread: true,
    },
    {
      id: 2,
      message: "Market update published",
      time: "1 hour ago",
      unread: true,
    },
    {
      id: 3,
      message: "Your portfolio has been updated",
      time: "3 hours ago",
      unread: false,
    },
  ]);

  // Get display name
  const getDisplayName = () => {
    const firstName = userData.firstName || "";
    const lastName = userData.lastName || "";
    const name = `${firstName} ${lastName}`.trim();
    
    return name || userData.email || "User";
  };

  // Generate initials for avatar
  const getInitials = () => {
    const firstName = userData.firstName || "";
    const lastName = userData.lastName || "";
    const name = `${firstName} ${lastName}`.trim();
    
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    }
    
    return userData.email
      ? userData.email.substring(0, 2).toUpperCase()
      : "U";
  };

  const unreadCount = notifications.filter((n) => n.unread).length;

  const isUserAdmin = () => {
    const roles = Array.isArray(userData?.roles) ? userData.roles : [];
    return roles.some((role) => role && role.toLowerCase() === "admin");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Implement search functionality
      console.log("Searching for:", searchQuery);
    }
  };

  const handleNotificationClick = (notification) => {
    console.log("Notification clicked:", notification);
    // Implement notification handling
  };

  const handleUserMenuClick = () => {
    // Navigate based on user role
    const admin = isUserAdmin();
    console.log("ModernHeader - User is admin:", admin);

    if (admin) {
      console.log("ModernHeader - Navigating to admin profile");
      navigate("/admin/profile");
    } else {
      console.log("ModernHeader - Navigating to regular user profile");
      navigate("/d/profile");
    }
  };

  return (
    <header className="modern-header">
      <div className="header-left">
        {/* Sidebar Toggle */}
        <button
          className="toggle-sidebar-btn"
          onClick={() => {
            console.log(
              "Toggle button clicked, current collapsed state:",
              collapsed
            );
            onToggleSidebar();
          }}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "40px",
            minHeight: "40px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
          }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </button>

        {/* Back Button */}
        {onGoBack && (
          <button
            className="btn btn-secondary"
            onClick={onGoBack}
            title="Go back"
          >
            <LeftOutlined />
            <span>Back</span>
          </button>
        )}

        {/* Page Title */}
        <div className="page-title">
          <h1 className="content-title">{title}</h1>
          {/* <p className="content-subtitle">{subtitle}</p> */}
        </div>
      </div>

      <div className="header-right">
        {/* Search */}
        <form onSubmit={handleSearch} className="search-container">
          <SearchOutlined className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search bonds, companies, or news..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        {/* Theme Toggle */}
        <button
          className="btn btn-ghost theme-toggle"
          onClick={toggleTheme}
          title={`Switch to ${isDark ? "light" : "dark"} mode`}
        >
          {isDark ? <SunOutlined /> : <MoonOutlined />}
        </button>

        {/* Notifications */}
        <div className="notifications-container">
          <button
            className="btn btn-ghost notification-btn"
            title="Notifications"
          >
            <BellOutlined />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          <div className="notifications-dropdown">
            <div className="notifications-header">
              <h3>Notifications</h3>
              <button className="btn btn-ghost btn-sm">Mark all as read</button>
            </div>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.unread ? "unread" : ""
                    }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    <p className="notification-message">
                      {notification.message}
                    </p>
                    <span className="notification-time">
                      {notification.time}
                    </span>
                  </div>
                  {notification.unread && <div className="notification-dot" />}
                </div>
              ))}
            </div>
            <div className="notifications-footer">
              <button className="btn btn-primary btn-sm">
                View all notifications
              </button>
            </div>
          </div>
        </div>

        {/* User Menu */}
        <div className="user-menu-container">
          <button
            className="btn btn-ghost user-menu-btn"
            onClick={handleUserMenuClick}
            title="User menu"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "8px 16px",
              borderRadius: "12px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              transition: "all 0.2s ease",
              minWidth: "auto"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
            }}
          >
            <div 
              className="user-avatar header-user-avatar"
            >
              {userData.profile ? (
                <img 
                  src={userData.profile} 
                  alt="Profile" 
                />
              ) : (
                <div className="avatar-fallback">
                  {getInitials()}
                </div>
              )}
            </div>
            <div 
              className="user-info"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: 0
              }}
            >
              <span 
                className="user-name"
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: isDark ? "#ffffff" : "#1f2937",
                  lineHeight: "1.2",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "150px"
                }}
              >
                {getDisplayName()}
              </span>
              <span 
                className="user-role"
                style={{
                  fontSize: "11px",
                  fontWeight: "400",
                  color: isDark ? "#9ca3af" : "#6b7280",
                  lineHeight: "1.2",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "150px"
                }}
              >
                {isUserAdmin() ? "Administrator" : "User"}
              </span>
            </div>
          </button>
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
